import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon, GoBackIcon } from '@/ui/icons';
import { useVisualViewport } from '@/ui/layout/useVisualViewport';
import styles from './FullScreenPage.module.css';

export interface FullScreenPageProps {
  title: string;
  /** A small line above the title saying what the page is, "Lab Notes" over the case number. */
  kicker?: string;
  lead?: string;
  /** Back: returns to whatever was underneath, as it was left. */
  onClose: () => void;
  closeLabel: string;
  /**
   * How the way out is drawn. `back`, the default, is the arrow at the start of
   * the header: a page gone into, as Lab Notes is. `close` is a cross at the
   * end of it, after the action, where every sheet and drawer has theirs: a
   * view opened over the screen, as the enlarged teeth chart is. Either way it
   * is one button, named by `closeLabel`. The request of 5 October 2026
   * (third), section 5.
   */
  closeIcon?: 'back' | 'close';
  /** Something small at the end of the header, such as the refresh indicator. */
  action?: ReactNode;
  /** Pinned at the bottom, directly on the keyboard when it is open. */
  footer?: ReactNode;
  children: ReactNode;
  /**
   * A conversation: opened at its end, and kept at its end when the keyboard
   * opens or closes, unless the reader has scrolled up to read.
   */
  pinToEnd?: boolean;
}

/**
 * A page over the whole screen, for a phone. Request of 4 October 2026
 * (second), section 4: Lab Notes opens as one on a phone, in place of the
 * bottom sheet that left a gap above the keyboard.
 *
 * It lies over the screen it was opened from rather than replacing it, so Back
 * returns to the case exactly where it was scrolled to.
 */
export function FullScreenPage({
  title,
  kicker,
  lead,
  onClose,
  closeLabel,
  closeIcon = 'back',
  action,
  footer,
  children,
  pinToEnd = false,
}: FullScreenPageProps) {
  const titleId = useId();
  const kickerId = useId();
  const frame = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const atEnd = useRef(true);

  const keyboard = useVisualViewport(frame, () => {
    if (pinToEnd && atEnd.current) {
      const node = body.current;
      if (node !== null) node.scrollTop = node.scrollHeight;
    }
  });

  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /*
   * Drawn in the body, not where it was opened. Request of 6 October 2026: on
   * an iPhone the page's own scroller is a layer of its own, and a sheet inside
   * it sat under the tab bar, its buttons hidden behind it. In the body it is
   * above everything, as its z-index says. React still sees it where it was
   * written, so clicks and keys reach the same handlers as before.
   */
  return createPortal(
    <div
      ref={frame}
      className={`${styles.page} ${keyboard ? styles.keyboard : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={kicker === undefined ? titleId : `${kickerId} ${titleId}`}
    >
      <header className={styles.head}>
        {closeIcon === 'back' && (
          <button type="button" className={styles.back} aria-label={closeLabel} onClick={onClose}>
            <GoBackIcon size={20} />
          </button>
        )}
        <div className={closeIcon === 'close' ? `${styles.titles} ${styles.titlesFirst}` : styles.titles}>
          {kicker !== undefined && (
            <span id={kickerId} className={styles.kicker}>
              {kicker}
            </span>
          )}
          <h1 id={titleId}>{title}</h1>
          {lead !== undefined && <p>{lead}</p>}
        </div>
        {action}
        {closeIcon === 'close' && (
          <button type="button" className={styles.close} aria-label={closeLabel} onClick={onClose}>
            <CloseIcon size={20} />
          </button>
        )}
      </header>
      <div
        ref={body}
        className={styles.body}
        onScroll={(event) => {
          const node = event.currentTarget;
          atEnd.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80;
        }}
      >
        {children}
      </div>
      {footer !== undefined && <div className={styles.foot}>{footer}</div>}
    </div>,
    document.body,
  );
}
